/**
 * Field Test Modal - Full View with Activity Filters
 * Handles modal open/close and activity-based filtering
 */

class FieldTestModal {
    /**
     * @param {string} modalId - The modal element ID
     * @param {Object} translations - Translated strings
     */
    constructor(modalId, translations = {}) {
        this.modalId = modalId;
        this.modal = document.getElementById(modalId);
        this.translations = translations;
        this.activeFilter = 'all';
        this.previousActiveElement = null;

        if (!this.modal) {
            console.warn(`FieldTestModal: Modal #${modalId} not found`);
            return;
        }

        this.overlay = this.modal.querySelector('.field-test-modal__overlay');
        this.container = this.modal.querySelector('.field-test-modal__container');
        this.closeBtn = this.modal.querySelector('.field-test-modal__close');
        this.filterBtns = this.modal.querySelectorAll('.field-test-modal__filter-btn');
        this.cards = this.modal.querySelectorAll('.field-test-modal-card');

        this.#init();
    }

    #init() {
        this.#bindOpenTriggers();
        this.#bindCloseEvents();
        this.#bindFilterEvents();
        this.#bindKeyboardEvents();
    }

    #bindOpenTriggers() {
        const triggers = document.querySelectorAll(`[data-modal-target="${this.modalId}"]`);

        for (const trigger of triggers) {
            trigger.addEventListener('click', (e) => {
                e.preventDefault();
                this.open();
            });
        }
    }

    #bindCloseEvents() {
        if (this.closeBtn) {
            this.closeBtn.addEventListener('click', () => this.close());
        }

        if (this.overlay) {
            this.overlay.addEventListener('click', () => this.close());
        }
    }

    #bindFilterEvents() {
        for (const btn of this.filterBtns) {
            btn.addEventListener('click', () => {
                const filter = btn.dataset.filter;
                this.#setActiveFilter(filter);
                this.#filterCards(filter);
            });
        }
    }

    #bindKeyboardEvents() {
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.modal.classList.contains('is-open')) {
                this.close();
            }
        });
    }

    #setActiveFilter(filter) {
        this.activeFilter = filter;

        for (const btn of this.filterBtns) {
            btn.classList.toggle('is-active', btn.dataset.filter === filter);
        }
    }

    #filterCards(filter) {
        for (const card of this.cards) {
            const cardActivity = card.dataset.activity;
            const shouldShow = filter === 'all' || cardActivity === filter;
            card.classList.toggle('is-hidden', !shouldShow);
        }
    }

    open() {
        this.previousActiveElement = document.activeElement;
        this.modal.classList.add('is-open');
        document.body.style.overflow = 'hidden';

        // Focus first focusable element in modal
        const focusable = this.container.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (focusable) {
            focusable.focus();
        }
    }

    close() {
        this.modal.classList.remove('is-open');
        document.body.style.overflow = '';

        // Restore focus to trigger element
        if (this.previousActiveElement) {
            this.previousActiveElement.focus();
        }
    }
}

// Global instances registry
if (typeof window.fieldTestModalInstances === 'undefined') {
    window.fieldTestModalInstances = {};
}

// Export for use in Liquid templates
window.FieldTestModal = FieldTestModal;
